import React, { useEffect, useRef } from "react";
import { Container } from "@pixi/react";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import LoadingErrorPopupController from "./LoadingErrorPopupController";

/**
 * LoadingErrorPopup — shown when the game fails to initialise.
 *
 * Mounted by Stage on the error state alone, and Stage mounts nothing else
 * once that state is set. It used to live inside the loader, which is what
 * allowed the failure to be shown and then walked straight past: the loader's
 * hand-off ran on its own schedule and carried on into the intro popup with
 * the error still on screen.
 *
 * There is no dismiss. The only way out is the Reload button, because there is
 * no game to return to.
 */
const LoadingErrorPopup = () => {
  const { dimensions, device, orientation, isMobileSmall } = useDimensions();
  const containerRef = useRef(null);
  const controllerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current || controllerRef.current) return;

    controllerRef.current = new LoadingErrorPopupController(
      containerRef.current,
      dimensions,
      { device, orientation, isMobileSmall },
    );
    controllerRef.current.showError();

    return () => {
      if (controllerRef.current) {
        controllerRef.current.cleanup();
        controllerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    controllerRef.current?.updateDimensions(dimensions, {
      device,
      orientation,
      isMobileSmall,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dimensions, device, orientation, isMobileSmall]);

  return (
    <Container
      name="loading_error_popup"
      ref={containerRef}
      x={0}
      y={0}
      // Above the loader (10001) — on the paths where the loader is still up,
      // this has to cover it.
      zIndex={10002}
      eventMode="static"
      hitArea={{ contains: () => true }}
      sortableChildren
    />
  );
};

export default LoadingErrorPopup;
